import type { SubscriptionPlansMap } from '~/components-shop/JsonProvider';

/**
 * Find the best subscription plan for a given base oNumber.
 * Prioritizes the "1M" (1 month) variant. Returns the plan oNumber or null.
 */
export const findBestPlanONumber = (
  subscriptionPlans: SubscriptionPlansMap | undefined,
  oNumber: string
): string | null => {
  if (!subscriptionPlans) return null;
  const matching = Object.keys(subscriptionPlans).filter(
    (key) => subscriptionPlans[key].baseONumber === oNumber
  );
  if (matching.length === 0) return null;
  const preferred = matching.find((key) => key === `${oNumber}-1M`);
  return preferred ?? matching[0];
};

/**
 * Build a human-readable delivery interval string from interval type and amount.
 */
export const getDeliveryIntervalText = (interval: string, amount: number): string | null => {
  if (amount === 1) {
    switch (interval) {
      case 'MONTH': return 'Lieferung einmal im Monat';
      case 'WEEK': return 'Lieferung einmal pro Woche';
      case 'YEAR': return 'Lieferung einmal im Jahr';
      case 'DAY': return 'Lieferung einmal am Tag';
      default: return null;
    }
  }
  switch (interval) {
    case 'MONTH': return `Lieferung alle ${amount} Monate`;
    case 'WEEK': return `Lieferung alle ${amount} Wochen`;
    case 'YEAR': return `Lieferung alle ${amount} Jahre`;
    case 'DAY': return `Lieferung alle ${amount} Tage`;
    default: return null;
  }
};

/**
 * Build delivery interval text from subscription plans map and a plan oNumber.
 */
export const getDeliveryIntervalTextFromPlan = (
  subscriptionPlans: SubscriptionPlansMap | undefined,
  planONumber: string | null
): string | null => {
  if (!subscriptionPlans || !planONumber || !subscriptionPlans[planONumber]) {
    return null;
  }
  const { interval, amount } = subscriptionPlans[planONumber];
  return getDeliveryIntervalText(interval, amount);
};

/** Map single-letter suffix codes to interval types */
const SUFFIX_INTERVAL_MAP: Record<string, string> = {
  'M': 'MONTH',
  'W': 'WEEK',
  'Y': 'YEAR',
  'D': 'DAY',
};

/**
 * Parse a product number suffix (e.g. "O608-30-1M" → 1 MONTH, "O608-30-3D" → 3 DAY)
 * and return the delivery interval text.
 */
export const getDeliveryIntervalTextFromProductNumber = (productNumber: string | undefined): string | null => {
  if (!productNumber) return null;
  const match = productNumber.match(/-(\d+)([MWYD])$/);
  if (!match) return null;
  const amount = parseInt(match[1], 10);
  const interval = SUFFIX_INTERVAL_MAP[match[2]];
  if (isNaN(amount) || !interval) return null;
  return getDeliveryIntervalText(interval, amount);
};
